import {Box, Grid} from "@mui/system";
import style from "../../assets/css/proposal.module.scss";
import {Link, useNavigate} from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Button,
  Breadcrumbs,
  Typography,
} from "@mui/material";
import {useFormik} from "formik";
import * as Yup from "yup";
import {useCallback, useEffect, useMemo, useState} from "react";
import {HiCurrencyDollar} from "react-icons/hi2";
import GlobalDateField from "../global-fields/GlobalDateField";
import GlobalTextField from "../global-fields/GlobalTextField";
import GlobalSelectField from "../global-fields/GlobalSelectField";
import GlobalModal from "../global-fields/GlobalModal";
import {useDispatch, useSelector} from "react-redux";
import {selectAuthState} from "../../store/authSlice";
import {selectDataStoreState, setDataStore} from "../../store/dataSlice";
import {SiAdobeacrobatreader} from "react-icons/si";
import {ERR_MESSAGE, MANDATORY_QUES} from "../../constants/constants";
import GlobalNotificationPopup from "../global-fields/GlobalNotificationPopup";
import {removeCookies} from "../../constants/helper";
import {selectUiState, setUiData} from "../../store/uiSlice";
import {ENDORSEMENTS, REQUEST_ENDORSEMENT} from "../../constants/apiConstant";
import dayjs from "dayjs";
import GlobalTextAriaField from "../global-fields/GlobalTextArea";

const EndorseTypeMenuItems = [
  {name: "Entrustment", value: "Entrustment"},
  {name: "Exhibition", value: "Exhibition"},
  {name: "Stock Limit", value: "Stock Limit"},
  {name: "Shipping", value: "Shipping"},
  {name: "Other", value: "Other"},
];

const EndorsementFormModal = ({fetchListAgain}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {user} = useSelector(selectAuthState);
  const {endorsementParentProposal} = useSelector(selectDataStoreState);
  const {endorsementFormModal} = useSelector(selectUiState);

  const initialValues = {
    type: "",
    reason: "",
    date_from: "",
    date_to: "",
    additional_details: "",
  };

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: Yup.object({
      type: Yup.string().required(MANDATORY_QUES),
      reason: Yup.string().when("type", {
        is: "Other",
        then: (schema) => schema.required(MANDATORY_QUES),
        otherwise: (schema) => schema.notRequired(),
      }),
      date_from: Yup.string().required(MANDATORY_QUES),
      date_to: Yup.string().required(MANDATORY_QUES),
      additional_details: Yup.string().required(MANDATORY_QUES),
    }),

    onSubmit: async (values, {resetForm}) => {
      dispatch(setUiData({showLoader: true}));
      let data = {
        ...values,
        date_from: dayjs(values?.date_from).format("MM-DD-YYYY"),
        date_to: dayjs(values?.date_to).format("MM-DD-YYYY"),
        proposal_id: endorsementParentProposal?.proposal_id,
        ...(user?.user_type === "agent"
          ? {user_id: endorsementParentProposal.user_id, agent_id: user.id}
          : {user_id: user.id}),
      };
      // console.log("ENDORSEMENT data:", data);
      // dispatch(setUiData({showLoader: false}));

      try {
        const response = await fetch(REQUEST_ENDORSEMENT.url, {
          method: REQUEST_ENDORSEMENT.method,
          headers: REQUEST_ENDORSEMENT.headers,
          body: JSON.stringify(data),
        });

        const res = await response.json();

        dispatch(setUiData({showLoader: false}));

        if (res.status == 1) {
          GlobalNotificationPopup({type: "success", message: res?.message});
          resetForm();
        } else {
          GlobalNotificationPopup({type: "error", message: res?.message});
        }
        handleCloseEndorseForm();
      } catch (error) {
        handleCloseEndorseForm();
        console.error("Error:", error);
        removeCookies("isLogin");
        dispatch(setUiData({showLoader: false}));
        GlobalNotificationPopup({
          type: "error",
          message: ERR_MESSAGE,
        });
        navigate("/");
      }
    },
  });

  const handleCloseEndorseForm = () => {
    formik.resetForm();
    fetchListAgain();
    dispatch(
      setUiData({
        endorsementFormModal: false,
      })
    );
  };

  return (
    <>
      <GlobalModal
        open={endorsementFormModal}
        handleClose={handleCloseEndorseForm}
        aria-labelledby="Endorsement-form"
        closeIcon={true}
        title={"Endorsement Form"}
        subtitle={`Policy Number: ${endorsementParentProposal?.policy_number}`}
        dialogTitleClass={style.claimPopTitle}
        // disableBackdropClick={true}
        dialogContent={
          <Grid container spacing={2}>
            <Grid size={{xs: 12, md: 12}} className={style.claimPopMain}>
              <form
                autoComplete="false"
                autoSave="false"
                onSubmit={formik.handleSubmit}
              >
                <Grid container spacing={2}>
                  <Grid size={{xs: 12, md: 12}}>
                    <Box sx={{mb: 1, color: "#000"}}>Type of Endorsement*</Box>
                    <GlobalSelectField
                      labelId={`type`}
                      value={formik.values?.type}
                      onChange={(e) =>
                        formik.setFieldValue(`type`, e.target.value)
                      }
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched?.type && Boolean(formik.errors?.type)
                      }
                      helperText={
                        formik.touched?.type ? formik.errors?.type : ""
                      }
                      placeH={"Select"}
                      menuItems={EndorseTypeMenuItems}
                    />
                  </Grid>
                  <Grid size={{xs: 12, md: 12}}>
                    <Box sx={{mb: 1, color: "#000"}}>
                      Please specify if other
                    </Box>
                    <GlobalTextAriaField
                      row={2}
                      placeH="Type here..."
                      id="reason"
                      name="reason"
                      type="reason"
                      value={formik.values.reason}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.reason && Boolean(formik.errors.reason)
                      }
                      helperText={formik.touched.reason && formik.errors.reason}
                    />
                  </Grid>
                  <Grid size={{xs: 12, lg: 6, sm: 6}}>
                    <Box sx={{mb: 1, color: "#000"}}>Date From*</Box>
                    <GlobalDateField
                      name={`date_from`}
                      value={formik.values.date_from}
                      onChange={(newValue) =>
                        formik.setFieldValue(`date_from`, newValue)
                      }
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched?.date_from &&
                        Boolean(formik.errors?.date_from)
                      }
                      helperText={
                        formik.touched?.date_from && formik.errors?.date_from
                      }
                      disablePast
                      clearBtn={formik.values.date_from !== ""}
                    />
                  </Grid>
                  <Grid size={{xs: 12, lg: 6, sm: 6}}>
                    <Box sx={{mb: 1, color: "#000"}}>Date To*</Box>
                    <GlobalDateField
                      name={`date_to`}
                      value={formik.values.date_to}
                      dependentMinValue={formik.values.date_from}
                      onChange={(newValue) =>
                        formik.setFieldValue(`date_to`, newValue)
                      }
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched?.date_to &&
                        Boolean(formik.errors?.date_to)
                      }
                      helperText={
                        formik.touched?.date_to && formik.errors?.date_to
                      }
                      disablePast={true}
                      disableFuture={false}
                      disabled={!formik.values.date_from}
                      clearBtn={formik.values.date_to !== ""}
                    />
                  </Grid>

                  <Grid size={{xs: 12, md: 12}}>
                    <Box sx={{mb: 1, color: "#000"}}>Additional Details*</Box>

                    <GlobalTextAriaField
                      row={2}
                      placeH="Type here..."
                      id="additional_details"
                      name="additional_details"
                      type="additional_details"
                      value={formik.values.additional_details}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.additional_details &&
                        Boolean(formik.errors.additional_details)
                      }
                      helperText={
                        formik.touched.additional_details &&
                        formik.errors.additional_details
                      }
                    />
                  </Grid>

                  <Button
                    className={style.claimPopBtn}
                    variant="contained"
                    autoFocus
                    type="submit"
                  >
                    Submit
                  </Button>
                </Grid>
              </form>
            </Grid>
          </Grid>
        }
        // dialogActions={
        //     <Button
        //         className={style.claimPopBtn}
        //         variant="contained"
        //         onClick={handleSubmit}
        //     >
        //         Submit
        //     </Button>
        // }
      ></GlobalModal>
    </>
  );
};

export default EndorsementFormModal;
